import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send,
  Loader2,
  MessageCircle,
  Trash2,
  Plus,
  MessageSquare,
  CheckCircle2,
  Circle,
  Pencil,
  X,
  Check,
  Share2,
  BarChart2,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import ChatShareModal from './components/ChatShareModal';
import ChatFormWidget, {
  type BackendPlan,
  type BackendForm,
  type BackendMessageType,
  type FormResponse,
  PlanCard,
} from './components/ChatFormWidget';
import SimulationResultsContent from '../simulationResults/SimulationResultsContent';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { wiringValue } from '../../types/subscription';
import Sidebar from '../../components/Sidebar';
import { useSimulationTimeEstimate } from '../../hooks/useSimulationTimeEstimate';
import { SEGMENT_IDS } from '../../config/segments';
import { refusalMessage } from '../../lib/chatRefusal';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

// Segment IDs resolve in src/config/segments.ts (single source of truth). These
// previously carried this file's own fallbacks, which disagreed with the other chat
// surfaces (e.g. NATURAL_ORGANIC 222 here vs 955 elsewhere, and a stale SmartyPants 400).
const NATURAL_ORGANIC_ID = SEGMENT_IDS.NATURAL_ORGANIC;
const FLAVCITY_ID = SEGMENT_IDS.FLAVCITY;
const KNF_SURVEY_ID = SEGMENT_IDS.KNF_SURVEY;
const SMARTYPANTS_ID = SEGMENT_IDS.SMARTYPANTS;

const AVAILABLE_SEGMENTS = [
  { id: NATURAL_ORGANIC_ID, name: 'Natural Organic Shoppers', icon: MessageCircle },
  { id: FLAVCITY_ID, name: 'FlavCity Protein Consumers', icon: MessageCircle },
  { id: KNF_SURVEY_ID, name: 'Kettle & Fire Survey', icon: MessageCircle },
  { id: SMARTYPANTS_ID, name: 'SmartyPants Consumers', icon: MessageCircle },
];

const DEFAULT_SEGMENT_ID = NATURAL_ORGANIC_ID;

// ── Types ────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  personas_cited?: string[];
  format?: string;
  progress_snapshot?: StructuredProgress;
  // Server-driven form state for specialist flows
  messageType?: BackendMessageType;
  plan?: BackendPlan;
  form?: BackendForm | null;
  activeStepId?: string | null;
  // True once user has submitted this step (freezes the widget to read-only plan)
  formSubmitted?: boolean;
}

interface ChatSession {
  id: number;
  title: string | null;
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
}

interface StageInfo {
  stage: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
  group?: 'planning' | 'output';
}

interface PlanStepProgress {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'skipped';
}

interface StructuredProgress {
  current_stage: string;
  stages: StageInfo[];
  plan_steps?: PlanStepProgress[];
  plan_version: number;
  message: string;
}

interface TaskStatusResp {
  task_id: string;
  state: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
  progress?: number;
  message?: string;
  structured_progress?: StructuredProgress;
  result?: {
    response: string;
    format: string;
    personas_cited: string[];
  };
  error?: string;
}

// ── Time-based progress ──────────────────────────────────────────────────

const calculateProgress = (
  startTime: number,
  maxProgress: number = 80,
  durationMs: number = 60000,
): number => {
  const elapsed = Date.now() - startTime;
  const t = Math.min(elapsed / durationMs, 1);
  const eased = 1 - Math.pow(1 - t, 2);
  return eased * maxProgress;
};

const FAST_DURATION = 60000;
const SLOW_DURATION = 240000;

const TimeBasedProgressBar: React.FC<{
  taskId: string;
  startTime: number | undefined;
  isCompleted: boolean;
}> = ({ taskId, startTime, isCompleted }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const maxReachedRef = useRef(0);
  const effectiveStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isCompleted) {
      const animateTo100 = () => {
        setDisplayProgress((prev) => {
          if (prev >= 99.5) {
            maxReachedRef.current = 100;
            return 100;
          }
          const next = prev + (100 - prev) * 0.2;
          maxReachedRef.current = Math.max(maxReachedRef.current, next);
          return next;
        });
      };
      const interval = setInterval(animateTo100, 50);
      setTimeout(() => {
        clearInterval(interval);
        setDisplayProgress(100);
        maxReachedRef.current = 100;
      }, 500);
      return () => clearInterval(interval);
    }

    if (!effectiveStartTimeRef.current) {
      effectiveStartTimeRef.current = startTime || Date.now();
    }
    const effectiveStartTime = effectiveStartTimeRef.current;

    const isSlowTask = taskId === 'final_output';
    const duration = isSlowTask ? SLOW_DURATION : FAST_DURATION;
    const hashCode = taskId.split('').reduce((a, b) => (a << 5) - a + b.charCodeAt(0), 0);
    const maxProgress = isSlowTask ? 90 + (Math.abs(hashCode) % 8) : 75 + (Math.abs(hashCode) % 10);

    const interval = setInterval(() => {
      const targetProgress = calculateProgress(effectiveStartTime, maxProgress, duration);
      const newProgress = Math.max(maxReachedRef.current, targetProgress);
      maxReachedRef.current = newProgress;
      setDisplayProgress((prev) => {
        const diff = newProgress - prev;
        if (Math.abs(diff) < 0.3) return newProgress;
        return prev + diff * 0.2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [startTime, isCompleted, taskId]);

  const showProgress = Math.round(displayProgress);

  return (
    <div className="mt-2">
      <div className="h-1.5 bg-[#07E5D1]/20 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#07E5D1] rounded-full transition-all duration-300 ease-out"
          style={{ width: `${showProgress}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <p className="text-[10px] text-[#07E5D1] flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {showProgress < 30
            ? 'Starting...'
            : showProgress < 60
              ? 'Processing...'
              : 'Almost there...'}
        </p>
        <span className="text-[10px] text-gray-400">{showProgress}%</span>
      </div>
    </div>
  );
};

// ── Markdown components ──────────────────────────────────────────────────

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>
  ),
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-[#07E5D1] hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>
  ),
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

// ── Inline report ────────────────────────────────────────────────────────

const InlineReport: React.FC<{ simulationId: number }> = ({ simulationId }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mt-4 border border-[#E5E7EB] rounded-2xl bg-white shadow-sm overflow-hidden w-full">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#FAFBFC] transition-colors border-b border-[#F1F5F9]"
      >
        <div className="flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-[#07E5D1]" />
          <span className="text-sm font-semibold text-[#1C1C1E]">Report</span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && <SimulationResultsContent simulationId={simulationId} hideChat hideHeader />}
    </div>
  );
};

// ── Form-flow collapser ──────────────────────────────────────────────────
// During a live session the frontend updates ONE plan card in-place as the
// user fills each step. The backend, however, persists each step response as
// a separate message. On reload that produces multiple plan bubbles.
// Fix: discard every plan-bearing message that has a later sibling with a
// plan — keep only the last (most-complete) one, mirroring live behaviour.
function collapseFormFlow(msgs: ChatMessage[]): ChatMessage[] {
  return msgs.filter((msg, idx) => {
    if (msg.role !== 'assistant' || !msg.plan) return true;
    return !msgs.slice(idx + 1).some((m) => m.role === 'assistant' && m.plan);
  });
}

// ── Message deserializer ─────────────────────────────────────────────────
// Converts a raw message object from the API into a ChatMessage.
// active_step_id is now authoritative from the backend (stored in metadata).
// messageType fallback inference is kept for older messages that predate the field.
function deserializeMessage(m: any): ChatMessage {
  let messageType: BackendMessageType | undefined = m.metadata?.message_type ?? undefined;
  const plan = m.metadata?.plan ?? undefined;
  const form = m.metadata?.form ?? undefined;
  const formSubmitted: boolean = m.metadata?.form_submitted ?? false;
  const activeStepId: string | null = m.metadata?.active_step_id ?? null;

  // Fallback: infer messageType from plan state for messages without stored type
  if (!messageType && plan) {
    const allDone = plan.steps.every((s: any) => s.status === 'done');
    const hasActive = plan.steps.some((s: any) => s.status === 'active');
    if (allDone && !form) {
      messageType = 'specialist_ready';
    } else if ((hasActive || activeStepId) && form) {
      messageType = 'specialist_collecting';
    }
  }

  // specialist_ready is only truly submitted after dispatch, not after canvas step submission
  const resolvedFormSubmitted = messageType === 'specialist_ready' ? false : formSubmitted;

  return {
    role: m.role,
    content: m.content,
    personas_cited: m.metadata?.personas_cited,
    format: m.metadata?.format,
    progress_snapshot: m.metadata?.progress_snapshot,
    messageType,
    plan,
    form,
    activeStepId,
    formSubmitted: resolvedFormSubmitted,
  };
}

// ── Main Component ───────────────────────────────────────────────────────

const ChatWithBluepill: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const { isAuthenticated, isAdmin } = useAuth();
  const { clientConfig } = useSubscription();

  // Which segment this account opens on. Served (client_wiring -> bluepill-chat/picker),
  // replacing three per-client ternaries and the ids they resolved through.
  const userDefaultSegmentId = wiringValue<number>(
    clientConfig,
    'bluepill-chat',
    'default_segment_id',
    DEFAULT_SEGMENT_ID,
  );
  const [selectedSegmentId, setSelectedSegmentId] = useState<number>(userDefaultSegmentId);

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  // ── Report panel state ───────────────────────────────────────────
  const [simulationId, setSimulationId] = useState<number | null>(null);
  const [simulationStatus, setSimulationStatus] = useState<
    'idle' | 'running' | 'completed' | 'failed'
  >('idle');
  const [reportPanelOpen, setReportPanelOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const simPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { estimate: simEstimate, remainingMinutes: simRemainingMinutes } =
    useSimulationTimeEstimate(
      simulationStatus === 'running' ? simulationId : null,
      simulationStatus === 'running',
    );
  // Set to true when simPollRef starts from applySessionData's already-resolved status check,
  // so the first immediate poll() in the effect doesn't double-fire or append duplicate messages.
  const simAlreadyResolvedRef = useRef(false);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Analyzing your question...');

  // Grey out input when specialist is collecting inputs or simulation is running
  const hasActiveForm = messages.some(
    (m) =>
      m.role === 'assistant' &&
      m.plan &&
      !m.formSubmitted &&
      (m.messageType === 'specialist_collecting' || m.messageType === 'specialist_ready'),
  );
  const inputDisabled = isLoading || hasActiveForm || simulationStatus === 'running';
  const [structuredProgress, setStructuredProgress] = useState<StructuredProgress | null>(null);
  const [stepStartTimes, setStepStartTimes] = useState<Record<string, number>>({});
  const [stageStartTimes, setStageStartTimes] = useState<Record<string, number>>({});
  const [lastProgressSnapshot, setLastProgressSnapshot] = useState<StructuredProgress | null>(null);
  const progressSnapshotRef = useRef<StructuredProgress | null>(null);

  const [animatedDots, setAnimatedDots] = useState('');
  useEffect(() => {
    if (!isLoading) {
      setAnimatedDots('');
      return;
    }
    const interval = setInterval(() => {
      setAnimatedDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);
    return () => clearInterval(interval);
  }, [isLoading]);

  const logOffsetRef = useRef(0);
  const logFileRef = useRef<string | null>(null);
  const logPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const chatScrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);

  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState<{
    share_token: string;
    share_url: string;
    message_count: number;
    view_count: number;
    fork_count: number;
  } | null>(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [isRevokingShare, setIsRevokingShare] = useState(false);
  const [formWidgetOpen, setFormWidgetOpen] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!isAuthenticated) navigate('/auth');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (simPollRef.current) clearInterval(simPollRef.current);
    };
  }, []);

  // ── Simulation polling ───────────────────────────────────────────
  // Only depends on simulationId — starts when a new ID is set, self-stops on terminal state.
  // simAlreadyResolvedRef: set by applySessionData when it already fetched the status inline,
  // so we skip the redundant immediate poll() and only run the interval for truly-running sims.
  useEffect(() => {
    if (!simulationId) return;
    if (simPollRef.current) clearInterval(simPollRef.current);

    // If applySessionData already resolved the status, skip the first immediate fetch.
    if (simAlreadyResolvedRef.current) {
      simAlreadyResolvedRef.current = false;
      // Only keep polling if the sim is still running.
      if (simulationStatus !== 'running') return;
    }

    const stopPoll = () => {
      clearInterval(simPollRef.current!);
      simPollRef.current = null;
    };

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/bluepill-chat/simulations/${simulationId}`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        const hasResponse = !!data.simulation_response && data.simulation_response !== '{}';
        const hasFailed = data.status === 'failed' || data.status === 'error';

        if (hasResponse) {
          stopPoll();
          setSimulationStatus((prev) => {
            if (prev === 'completed') return prev; // already set — don't append duplicate message
            setMessages((msgs) => [
              ...msgs,
              {
                role: 'assistant',
                content: 'Your report is ready! Click **View Report** in the top right to open it.',
              },
            ]);
            return 'completed';
          });
        } else if (hasFailed) {
          stopPoll();
          setSimulationStatus((prev) => {
            if (prev === 'failed') return prev;
            setMessages((msgs) => [
              ...msgs,
              {
                role: 'assistant',
                content: 'The simulation failed. Please try running it again.',
              },
            ]);
            return 'failed';
          });
        }
      } catch {
        /* ignore transient */
      }
    };

    poll();
    simPollRef.current = setInterval(poll, 5000);
    return () => {
      if (simPollRef.current) clearInterval(simPollRef.current);
    };
  }, [simulationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Session CRUD ─────────────────────────────────────────────────

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/bluepill-chat/sessions?segment_id=${selectedSegmentId}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch {
      /* ignore */
    }
  }, [selectedSegmentId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const checkSessionStatus = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(`${API_URL}/bluepill-chat/sessions/${sessionId}/status`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, []);

  const pollTaskStatusRef = useRef<((taskId: string) => Promise<void>) | null>(null);
  const startLogPollingRef = useRef<((sessionId: number) => void) | null>(null);

  const stopAllPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (logPollRef.current) {
      clearInterval(logPollRef.current);
      logPollRef.current = null;
    }
    currentTaskIdRef.current = null;
    setIsLoading(false);
    setStructuredProgress(null);
    setProgressMessage('');
  }, []);

  const createSession = useCallback(async () => {
    stopAllPolling();
    try {
      const res = await fetch(`${API_URL}/bluepill-chat/sessions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ segment_id: selectedSegmentId }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setActiveSessionId(data.id);
      setMessages([]);
      setSimulationId(null);
      setSimulationStatus('idle');
      setReportPanelOpen(false);
      await fetchSessions();
    } catch {
      /* ignore */
    }
  }, [fetchSessions, stopAllPolling, selectedSegmentId]);

  const applySessionData = useCallback((id: number, data: any) => {
    // Reset simulation state before applying new session
    setSimulationId(null);
    setSimulationStatus('idle');
    setReportPanelOpen(false);
    setActiveSessionId(id);
    setMessages(collapseFormFlow((data.messages || []).map(deserializeMessage)));

    // simulation_id comes from the dispatching message metadata in history
    const rawMsgs: any[] = data.messages || [];
    const dispatchRaw = rawMsgs.find((m: any) => m.metadata?.simulation_id);
    const simId: number | null = data.simulation_id ?? dispatchRaw?.metadata?.simulation_id ?? null;
    if (simId) {
      // Fetch status first so we can tell the poll effect whether to skip or continue.
      fetch(`${API_URL}/bluepill-chat/simulations/${simId}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const hasResponse = d && !!d.simulation_response && d.simulation_response !== '{}';
          const hasFailed = d && (d.status === 'failed' || d.status === 'error');
          const isRunning = !hasResponse && !hasFailed;
          // Tell the poll effect that we already resolved — skip redundant immediate fetch.
          simAlreadyResolvedRef.current = true;
          setSimulationStatus(isRunning ? 'running' : hasResponse ? 'completed' : 'failed');
          setSimulationId(simId); // triggers poll effect; effect reads simAlreadyResolvedRef
        })
        .catch(() => {
          simAlreadyResolvedRef.current = true;
          setSimulationStatus('running');
          setSimulationId(simId);
        });
    }
  }, []);

  const loadSession = useCallback(
    async (id: number) => {
      stopAllPolling();
      setIsSessionLoading(true);
      try {
        const res = await fetch(`${API_URL}/bluepill-chat/sessions/${id}`, {
          credentials: 'include',
        });
        if (!res.ok) {
          setIsSessionLoading(false);
          return;
        }
        const data = await res.json();
        applySessionData(id, data);
        const status = await checkSessionStatus(id);
        if (status?.has_active_task && status.task_id) {
          setIsLoading(true);
          setProgressMessage('Resuming...');
          setStructuredProgress({
            current_stage: 'resuming',
            message: 'Resuming...',
            plan_steps: [],
            stages: [],
            plan_version: 0,
          });
          currentTaskIdRef.current = status.task_id;
          if (pollRef.current) clearInterval(pollRef.current);
          const taskId = status.task_id;
          pollRef.current = setInterval(() => {
            pollTaskStatusRef.current?.(taskId);
          }, 1000);
          pollTaskStatusRef.current?.(taskId);
          startLogPollingRef.current?.(id);
        }
      } catch {
        /* ignore */
      }
      setIsSessionLoading(false);
    },
    [stopAllPolling, checkSessionStatus, applySessionData],
  );

  const hasInitializedFromUrlRef = useRef(false);

  useEffect(() => {
    if (hasInitializedFromUrlRef.current) return;
    if (!urlSessionId || sessions.length === 0) return;

    hasInitializedFromUrlRef.current = true;
    const sessionIdNum = parseInt(urlSessionId, 10);
    const sessionExists = sessions.some((s) => s.id === sessionIdNum);

    if (sessionExists) {
      setIsSessionLoading(true);
      (async () => {
        try {
          const res = await fetch(`${API_URL}/bluepill-chat/sessions/${sessionIdNum}`, {
            credentials: 'include',
          });
          if (!res.ok) {
            setIsSessionLoading(false);
            return;
          }
          const data = await res.json();
          applySessionData(sessionIdNum, data);
          const status = await checkSessionStatus(sessionIdNum);
          if (status?.has_active_task && status.task_id) {
            setIsLoading(true);
            setProgressMessage('Resuming...');
            setStructuredProgress({
              current_stage: 'resuming',
              message: 'Resuming...',
              plan_steps: [],
              stages: [],
              plan_version: 0,
            });
            currentTaskIdRef.current = status.task_id;
            if (pollRef.current) clearInterval(pollRef.current);
            const taskId = status.task_id;
            pollRef.current = setInterval(() => {
              pollTaskStatusRef.current?.(taskId);
            }, 1000);
            pollTaskStatusRef.current?.(taskId);
            startLogPollingRef.current?.(sessionIdNum);
          }
        } catch {
          /* ignore */
        }
        setIsSessionLoading(false);
      })();
    } else {
      navigate('/chat-with-bluepill', { replace: true });
    }
  }, [urlSessionId, sessions, navigate, checkSessionStatus, applySessionData]);

  useEffect(() => {
    if (!activeSessionId) return;
    const currentUrlId = urlSessionId ? parseInt(urlSessionId, 10) : null;
    if (currentUrlId !== activeSessionId) {
      navigate(`/chat-with-bluepill/${activeSessionId}`, { replace: true });
    }
  }, [activeSessionId, navigate, urlSessionId]);

  const deleteSession = useCallback(
    async (id: number) => {
      try {
        await fetch(`${API_URL}/bluepill-chat/sessions/${id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (activeSessionId === id) {
          stopAllPolling();
          setActiveSessionId(null);
          setMessages([]);
          navigate('/chat-with-bluepill', { replace: true });
        }
        await fetchSessions();
      } catch {
        /* ignore */
      }
    },
    [activeSessionId, fetchSessions, navigate, stopAllPolling],
  );

  const handleStartRename = useCallback((session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditingTitle(session.title || `Chat ${session.id}`);
  }, []);

  const handleSaveRename = useCallback(async () => {
    if (!editingSessionId || !editingTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    setIsRenaming(true);
    try {
      const res = await fetch(`${API_URL}/bluepill-chat/sessions/${editingSessionId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editingTitle.trim() }),
      });
      if (res.ok) await fetchSessions();
    } catch {
      /* ignore */
    }
    setIsRenaming(false);
    setEditingSessionId(null);
  }, [editingSessionId, editingTitle, fetchSessions]);

  const handleCancelRename = useCallback(() => {
    setEditingSessionId(null);
    setEditingTitle('');
  }, []);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveRename();
      } else if (e.key === 'Escape') {
        handleCancelRename();
      }
    },
    [handleSaveRename, handleCancelRename],
  );

  const handleDeleteClick = useCallback((id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingSessionId(id);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deletingSessionId) {
      await deleteSession(deletingSessionId);
      setDeletingSessionId(null);
    }
  }, [deletingSessionId, deleteSession]);

  const handleCancelDelete = useCallback(() => {
    setDeletingSessionId(null);
  }, []);

  // ── Share Functions ──────────────────────────────────────────────

  const fetchExistingShare = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(`${API_URL}/bluepill-chat/sessions/${sessionId}/share`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.has_share) {
        return {
          share_token: data.share_token,
          share_url: data.share_url,
          message_count: data.message_count,
          view_count: data.view_count || 0,
          fork_count: data.fork_count || 0,
        };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const handleShareClick = useCallback(async () => {
    if (!activeSessionId || messages.length === 0) return;
    setIsCreatingShare(true);
    try {
      const existing = await fetchExistingShare(activeSessionId);
      if (existing) {
        setShareData(existing);
        setShowShareModal(true);
        setIsCreatingShare(false);
        return;
      }

      const res = await fetch(`${API_URL}/bluepill-chat/sessions/${activeSessionId}/share`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allow_fork: true }),
      });
      if (!res.ok) {
        console.error('Failed to create share link');
        return;
      }

      const data = await res.json();
      setShareData({
        share_token: data.share_token,
        share_url: data.share_url,
        message_count: data.message_count,
        view_count: 0,
        fork_count: 0,
      });
      setShowShareModal(true);
    } catch (err) {
      console.error('Error creating share:', err);
    } finally {
      setIsCreatingShare(false);
    }
  }, [activeSessionId, messages.length, fetchExistingShare]);

  const handleRevokeShare = useCallback(async () => {
    if (!activeSessionId || !shareData) return;
    setIsRevokingShare(true);
    try {
      const res = await fetch(
        `${API_URL}/bluepill-chat/sessions/${activeSessionId}/share/${shareData.share_token}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );
      if (res.ok) {
        setShareData(null);
        setShowShareModal(false);
      }
    } catch (err) {
      console.error('Error revoking share:', err);
    } finally {
      setIsRevokingShare(false);
    }
  }, [activeSessionId, shareData]);

  useEffect(() => {
    setShareData(null);
    setShowShareModal(false);
  }, [activeSessionId]);

  // ── Log polling ──────────────────────────────────────────────────

  const pollLogs = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(
        `${API_URL}/bluepill-chat/sessions/${sessionId}/logs?offset=${logOffsetRef.current}`,
        { credentials: 'include' },
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.file && logFileRef.current && data.file !== logFileRef.current) {
        logOffsetRef.current = 0;
        logFileRef.current = data.file;
        return;
      }
      logFileRef.current = data.file ?? null;
      logOffsetRef.current = data.offset;
    } catch {}
  }, []);

  const startLogPolling = useCallback(
    (sessionId: number) => {
      if (logPollRef.current) clearInterval(logPollRef.current);
      logOffsetRef.current = 0;
      logFileRef.current = null;
      logPollRef.current = setInterval(() => pollLogs(sessionId), 1500);
      setTimeout(() => pollLogs(sessionId), 800);
    },
    [pollLogs],
  );

  const stopLogPolling = useCallback(() => {
    if (logPollRef.current) {
      clearInterval(logPollRef.current);
      logPollRef.current = null;
    }
  }, []);

  // ── Task status polling ──────────────────────────────────────────

  const pollTaskStatus = useCallback(
    async (taskId: string) => {
      try {
        const res = await fetch(`${API_URL}/bluepill-chat/status/${taskId}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('status poll failed');
        const status: TaskStatusResp = await res.json();

        if (status.state === 'PENDING' || status.state === 'PROGRESS') {
          setProgressMessage(status.message || 'Processing...');
          if (status.structured_progress) {
            const newStepStartTimes: Record<string, number> = { ...stepStartTimes };
            status.structured_progress.plan_steps?.forEach((step) => {
              if (step.status === 'running' && !newStepStartTimes[step.id]) {
                newStepStartTimes[step.id] = Date.now();
              }
            });
            const outputStage = status.structured_progress.stages.find((s) => s.stage === 'output');
            if (outputStage?.status === 'active' && !newStepStartTimes['final_output']) {
              newStepStartTimes['final_output'] = Date.now();
            }
            setStepStartTimes(newStepStartTimes);

            const newStageStartTimes: Record<string, number> = { ...stageStartTimes };
            status.structured_progress.stages.forEach((stage) => {
              if (stage.status === 'active' && !newStageStartTimes[stage.stage]) {
                newStageStartTimes[stage.stage] = Date.now();
              }
            });
            setStageStartTimes(newStageStartTimes);

            setStructuredProgress(status.structured_progress);
            setLastProgressSnapshot(status.structured_progress);
            progressSnapshotRef.current = status.structured_progress;
          }
        } else if (status.state === 'SUCCESS' && status.result) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            currentTaskIdRef.current = null;
          }
          stopLogPolling();
          const snapshotToUse = progressSnapshotRef.current || lastProgressSnapshot;
          const finalSnapshot: StructuredProgress | undefined = snapshotToUse
            ? {
                ...snapshotToUse,
                stages: snapshotToUse.stages.map((s) => ({ ...s, status: 'completed' as const })),
                plan_steps: [
                  ...(snapshotToUse.plan_steps?.map((s) => ({
                    ...s,
                    status: 'completed' as const,
                  })) || []),
                  {
                    id: 'final_output',
                    label: 'Preparing your answer',
                    status: 'completed' as const,
                  },
                ],
              }
            : undefined;

          const taskResult = status.result as any;
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: taskResult.response || '',
              personas_cited: taskResult.personas_cited,
              format: taskResult.format,
              progress_snapshot: finalSnapshot,
              messageType: taskResult.type ?? 'twin_chat',
              plan: taskResult.plan ?? undefined,
              form: taskResult.form ?? undefined,
              activeStepId: taskResult.active_step_id ?? null,
              formSubmitted: false,
            },
          ]);
          setTimeout(scrollToBottom, 50);
          setIsLoading(false);
          setStructuredProgress(null);
          setStepStartTimes({});
          setStageStartTimes({});
          setLastProgressSnapshot(null);
          progressSnapshotRef.current = null;
          await fetchSessions();
        } else if (status.state === 'FAILURE') {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            currentTaskIdRef.current = null;
          }
          stopLogPolling();
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: `Sorry, I encountered an error: ${status.error || 'Unknown error'}. Please try again.`,
            },
          ]);
          setTimeout(scrollToBottom, 50);
          setIsLoading(false);
          setStructuredProgress(null);
          setStepStartTimes({});
          setStageStartTimes({});
          setLastProgressSnapshot(null);
          progressSnapshotRef.current = null;
        }
      } catch {
        /* ignore transient errors */
      }
    },
    [
      activeSessionId,
      fetchSessions,
      stepStartTimes,
      stageStartTimes,
      lastProgressSnapshot,
      stopLogPolling,
      scrollToBottom,
    ],
  );

  useEffect(() => {
    pollTaskStatusRef.current = pollTaskStatus;
    startLogPollingRef.current = startLogPolling;
  }, [pollTaskStatus, startLogPolling]);

  // ── Send message ─────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    let sessionId = activeSessionId;
    if (!sessionId) {
      try {
        const res = await fetch(`${API_URL}/bluepill-chat/sessions`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ segment_id: selectedSegmentId }),
        });
        if (!res.ok) throw new Error('Could not create session');
        const data = await res.json();
        sessionId = data.id;
        setActiveSessionId(sessionId);
        await fetchSessions();
      } catch {
        return;
      }
    }

    const userMsg: ChatMessage = { role: 'user', content: input.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setTimeout(scrollToBottom, 50);
    setIsLoading(true);
    setProgressMessage('Sending query...');
    setStructuredProgress({
      current_stage: 'sending',
      message: 'Sending query...',
      plan_steps: [],
      stages: [],
      plan_version: 0,
    });
    setStepStartTimes({});
    setStageStartTimes({ sending: Date.now() });
    progressSnapshotRef.current = null;

    try {
      const res = await fetch(`${API_URL}/bluepill-chat/sessions/${sessionId}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: input.trim() }),
      });

      if (!res.ok) {
        // 401 is "we do not know who you are" — redirect. 403 is "we know, and the answer is no", which is a
        // MESSAGE: the chat cap refuses with 403 and a body naming the limit, and bouncing to /auth threw that
        // away and looked like the page reloading.
        if (res.status === 401) {
          navigate('/auth');
          return;
        }
        if (res.status === 403) {
          const refusal = await refusalMessage(res);
          setMessages((prev) => [...prev, { role: 'assistant', content: refusal }]);
          setIsLoading(false);
          setStructuredProgress(null);
          return;
        }
        throw new Error('Failed to send message');
      }

      const data = await res.json();
      const taskId = data.task_id;
      if (!taskId) throw new Error('No task ID received');

      currentTaskIdRef.current = taskId;
      pollRef.current = setInterval(() => pollTaskStatus(taskId), 1000);
      pollTaskStatus(taskId);
      startLogPolling(sessionId!);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
        },
      ]);
      setIsLoading(false);
      setStructuredProgress(null);
      setStageStartTimes({});
      setStepStartTimes({});
      progressSnapshotRef.current = null;
    }
  }, [
    input,
    isLoading,
    activeSessionId,
    fetchSessions,
    navigate,
    pollTaskStatus,
    startLogPolling,
    selectedSegmentId,
    scrollToBottom,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Form widget response handler ─────────────────────────────────
  // Called by ChatFormWidget after a successful POST /form.
  // Strategy:
  //   - The Plan+Canvas message is ALWAYS updated in-place (messageIdx).
  //     This keeps a single Plan card that live-updates as steps complete.
  //   - If the response carries a text commentary, it is appended as a
  //     separate plain assistant bubble ABOVE the plan (inserted just before it).
  const handleFormResponse = useCallback(
    (messageIdx: number, response: FormResponse) => {
      setMessages((prev) => {
        const next = [...prev];
        next[messageIdx] = {
          ...next[messageIdx],
          content: response.response ?? next[messageIdx].content,
          messageType: response.type,
          plan: response.plan ?? next[messageIdx].plan,
          form: response.form ?? null,
          activeStepId: response.active_step_id ?? null,
          formSubmitted: response.type === 'simulation_dispatching',
        };
        return next;
      });

      // Kick off simulation polling when dispatched
      if (response.type === 'simulation_dispatching' && response.simulation_id) {
        setSimulationId(response.simulation_id);
        setSimulationStatus('running');
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: "Simulation is running… I'll let you know when the report is ready.",
          },
        ]);
      }

      setTimeout(scrollToBottom, 50);
      fetchSessions();
    },
    [scrollToBottom, fetchSessions],
  );

  if (!isAuthenticated) return null;

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col md:ml-16 overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-[#F1F5F9] px-6 py-3 mt-16 md:mt-0">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
                <MessageCircle className="w-4.5 h-4.5" />
              </div>
              <div>
                <h1 className="font-semibold text-[#1C1C1E] text-sm">Chat with Bluepill</h1>
                <p className="text-xs text-[#9CA3AF]">Always On Focus Group</p>
              </div>
            </div>

            {isAdmin && (
              <div className="flex items-center gap-4">
                <select
                  value={selectedSegmentId}
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    if (newId === selectedSegmentId) return;
                    stopAllPolling();
                    setSelectedSegmentId(newId);
                    setActiveSessionId(null);
                    setMessages([]);
                  }}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-[#FAFBFC] text-[#374151] hover:border-[#07E5D1] transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#07E5D1]"
                >
                  {AVAILABLE_SEGMENTS.map((seg) => (
                    <option key={seg.id} value={seg.id}>
                      {seg.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center gap-3">
              {/* View Report button — visible once simulation is running or done */}
              {simulationStatus === 'running' && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[#9CA3AF] bg-[#FAFBFC] border border-[#E5E7EB]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Running…
                </div>
              )}
              {simulationStatus === 'completed' && simulationId && (
                <button
                  onClick={() => setReportPanelOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#07E5D1] hover:bg-[#06C4B4] transition-colors shadow-sm"
                >
                  <BarChart2 className="w-3.5 h-3.5" />
                  {reportPanelOpen ? 'Hide Report' : 'View Report'}
                </button>
              )}
              {activeSessionId && messages.length > 0 && (
                <button
                  onClick={handleShareClick}
                  disabled={isCreatingShare}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[#374151] bg-[#FAFBFC] border border-[#E5E7EB] hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors disabled:opacity-50"
                  title="Share this chat"
                >
                  {isCreatingShare ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Share2 className="w-3.5 h-3.5" />
                  )}
                  Share
                </button>
              )}
              <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
                <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="h-16" />
              </a>
            </div>
          </div>
        </header>

        {/* Body: sidebar + chat + optional report panel */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar — Sessions */}
          <aside
            className={`bg-white border-r border-[#F1F5F9] flex flex-col flex-shrink-0 transition-all duration-200 ${sidebarCollapsed ? 'w-10' : 'w-56'}`}
          >
            {sidebarCollapsed ? (
              /* Collapsed: just icon buttons stacked */
              <div className="flex flex-col items-center py-2 gap-2">
                <button
                  onClick={() => setSidebarCollapsed(false)}
                  className="p-2 rounded-lg text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#F0FDFB] transition-colors"
                  title="Expand sidebar"
                >
                  <PanelLeftOpen className="w-4 h-4" />
                </button>
                <button
                  onClick={createSession}
                  className="p-2 rounded-lg text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#F0FDFB] transition-colors"
                  title="New Chat"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="p-3 border-b border-[#F1F5F9] flex items-center gap-2">
                  <button
                    onClick={createSession}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-[#07E5D1] hover:bg-[#06D4C2] transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> New Chat
                  </button>
                  <button
                    onClick={() => setSidebarCollapsed(true)}
                    className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#F0FDFB] transition-colors flex-shrink-0"
                    title="Collapse sidebar"
                  >
                    <PanelLeftClose className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                  {sessions.map((s) => (
                    <div
                      key={s.id}
                      className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-xs ${
                        activeSessionId === s.id
                          ? 'bg-[#E8FCFA] text-[#07E5D1]'
                          : 'text-[#374151] hover:bg-[#FAFBFC]'
                      }`}
                      onClick={() => editingSessionId !== s.id && loadSession(s.id)}
                    >
                      <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                      {editingSessionId === s.id ? (
                        <div
                          className="flex-1 flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={handleRenameKeyDown}
                            autoFocus
                            disabled={isRenaming}
                            className="flex-1 px-1.5 py-0.5 text-xs bg-white border border-[#07E5D1] rounded focus:outline-none focus:ring-1 focus:ring-[#07E5D1] text-[#374151]"
                          />
                          <button
                            onClick={handleSaveRename}
                            disabled={isRenaming}
                            className="p-0.5 text-[#10B981] hover:text-[#059669] transition-colors"
                          >
                            {isRenaming ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )}
                          </button>
                          <button
                            onClick={handleCancelRename}
                            disabled={isRenaming}
                            className="p-0.5 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="flex-1 truncate">{s.title || `Chat ${s.id}`}</span>
                          <button
                            onClick={(e) => handleStartRename(s, e)}
                            className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#07E5D1] transition-all"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteClick(s.id, e)}
                            className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#EF4444] transition-all"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  {sessions.length === 0 && (
                    <p className="text-xs text-[#9CA3AF] text-center py-8">No sessions yet</p>
                  )}
                </div>
              </>
            )}
          </aside>

          {/* Center — Chat */}
          <main
            className={`flex flex-col min-w-0 overflow-hidden transition-all duration-300 ${reportPanelOpen ? 'w-1/2' : 'flex-1'}`}
          >
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto flex flex-col">
              <div className="flex-1" />
              <div className="max-w-[960px] mx-auto px-6 py-6 w-full">
                {isSessionLoading && (
                  <div className="flex items-center justify-center py-16">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 text-[#07E5D1] animate-spin" />
                      <p className="text-sm text-[#6B7280]">Loading conversation...</p>
                    </div>
                  </div>
                )}

                {!isSessionLoading && messages.length === 0 && !isLoading && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-5">
                      <MessageCircle className="w-8 h-8" />
                    </div>
                    <h2 className="text-xl font-bold text-[#1C1C1E] mb-2">Chat with Bluepill</h2>
                    <p className="text-sm text-[#6B7280] max-w-md mx-auto mb-6">
                      Ask any question and Bluepill will gather audience perspectives, analyze, and
                      deliver synthesized insights in minutes.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {(selectedSegmentId === SMARTYPANTS_ID
                        ? [
                            'What motivates vitamin and supplement purchases?',
                            'How do consumers evaluate supplement brands?',
                            'What are key factors when choosing gummy vitamins?',
                          ]
                        : selectedSegmentId === FLAVCITY_ID
                          ? [
                              'What motivates protein product purchases?',
                              'How do consumers evaluate protein brands?',
                              'What is the perception of FlavCity products in the market?',
                            ]
                          : [
                              'What motivates natural/organic purchases?',
                              'How do shoppers choose between brands?',
                              'What are common barriers to buying organic?',
                            ]
                      ).map((s, i) => (
                        <button
                          key={i}
                          onClick={() => setInput(s)}
                          className="px-3.5 py-2 bg-white border border-[#E5E7EB] rounded-xl text-xs text-[#6B7280] hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!isSessionLoading && (
                  <div className="space-y-5">
                    {messages.map((msg, idx) => {
                      // A form widget is "active" on the most recent unsubmitted specialist message
                      // (only while not currently loading a new response)
                      const hasFormWidget =
                        msg.role === 'assistant' &&
                        msg.plan &&
                        (msg.messageType === 'specialist_collecting' ||
                          msg.messageType === 'specialist_ready' ||
                          msg.messageType === 'intent_confirmation' ||
                          msg.messageType === 'simulation_dispatching');
                      // Only treat as active if this is the last assistant message —
                      // after dispatch a follow-up message always exists, so older
                      // plan cards are never erroneously interactive on refresh.
                      const isLastAssistantMsg = !messages
                        .slice(idx + 1)
                        .some((m) => m.role === 'assistant');
                      const isActiveForm =
                        hasFormWidget && !msg.formSubmitted && !isLoading && isLastAssistantMsg;

                      // Form-widget messages: collapsible header card + separate canvas bubble below
                      if (hasFormWidget && msg.plan && activeSessionId) {
                        // Default open during collection and after dispatch; user can toggle
                        const isOpen = formWidgetOpen[idx] !== false;
                        const toggleOpen = () =>
                          setFormWidgetOpen((prev) => ({ ...prev, [idx]: !isOpen }));
                        const doneCount = msg.plan.steps.filter((s) => s.status === 'done').length;

                        return (
                          <React.Fragment key={idx}>
                            {/* Collapsible header — light gray bg, always shown */}
                            <div className="border border-[#E5E7EB] rounded-2xl bg-[#F9FAFB] shadow-sm overflow-hidden w-full">
                              <button
                                onClick={toggleOpen}
                                className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#F3F4F6] transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <MessageCircle className="w-4 h-4 text-[#07E5D1]" />
                                  <span className="text-sm font-semibold text-[#1C1C1E]">
                                    {msg.plan.label}
                                  </span>
                                  {doneCount > 0 && (
                                    <span className="text-xs text-gray-400">
                                      {doneCount} input{doneCount !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>
                                <ChevronRight
                                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                                />
                              </button>
                              {isOpen && (
                                <div className="px-5 pb-4 pt-1 border-t border-[#F1F5F9]">
                                  <PlanCard plan={msg.plan} activeStepId={null} />
                                </div>
                              )}
                            </div>

                            {/* Canvas bubble — separate, only shown while actively collecting or ready to dispatch */}
                            {isActiveForm && msg.messageType !== 'intent_confirmation' && (
                              <div className="flex justify-start">
                                <div className="w-full bg-white border border-[#E5E7EB] text-[#374151] rounded-2xl rounded-bl-md shadow-sm px-5 py-4">
                                  <ChatFormWidget
                                    sessionId={activeSessionId}
                                    plan={msg.plan}
                                    form={msg.form ?? null}
                                    messageType={msg.messageType!}
                                    activeStepId={msg.activeStepId ?? null}
                                    isActive={true}
                                    onFormResponse={(response) => handleFormResponse(idx, response)}
                                  />
                                </div>
                              </div>
                            )}
                          </React.Fragment>
                        );
                      }

                      // Suppress types whose text content is intentionally hidden
                      const suppressedMsgTypes = [
                        'specialist_collecting',
                        'specialist_ready',
                        'simulation_dispatching',
                      ];
                      const contentVisible =
                        msg.role === 'user'
                          ? !!msg.content?.trim()
                          : !!msg.content?.trim() &&
                            !suppressedMsgTypes.includes(msg.messageType ?? '');
                      if (!contentVisible) return null;

                      return (
                        <React.Fragment key={idx}>
                          <div
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`rounded-2xl px-5 py-4 ${
                                msg.role === 'user'
                                  ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                                  : 'max-w-full bg-white border border-[#E5E7EB] text-[#374151] rounded-bl-md shadow-sm'
                              }`}
                            >
                              {msg.role === 'assistant' ? (
                                <div className="max-w-none [&>*:last-child]:mb-0">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={markdownComponents}
                                  >
                                    {msg.content}
                                  </ReactMarkdown>
                                </div>
                              ) : (
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}

                    {/* Time estimate bubble — shown while simulation is running */}
                    {simulationStatus === 'running' && simEstimate && (
                      <div className="flex justify-start">
                        <div className="bg-white border border-[#E5E7EB] text-[#374151] rounded-2xl rounded-bl-md shadow-sm px-5 py-4 max-w-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping flex-shrink-0" />
                            <span className="text-sm font-semibold text-[#1C1C1E]">
                              {simRemainingMinutes !== null && simRemainingMinutes > 1
                                ? `~${simRemainingMinutes} min remaining`
                                : simRemainingMinutes === 1
                                  ? 'Almost done…'
                                  : 'Finishing up — just a moment'}
                            </span>
                          </div>
                          {simEstimate.queueMinutes > 0 && (
                            <p className="text-xs text-gray-400 ml-4">
                              {simEstimate.baseMinutes} min processing + {simEstimate.queueMinutes}{' '}
                              min queue wait
                            </p>
                          )}
                          {simEstimate.queuedCount > 0 && (
                            <p className="text-xs text-gray-400 ml-4">
                              {simEstimate.queuedCount} simulation
                              {simEstimate.queuedCount > 1 ? 's' : ''} ahead in queue
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Inline report — full width, after all messages */}
                    {simulationStatus === 'completed' && simulationId && (
                      <InlineReport simulationId={simulationId} />
                    )}

                    {isLoading && (
                      <div className="flex justify-start">
                        {structuredProgress ? (
                          <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm w-full max-w-2xl overflow-hidden">
                            <div className="px-5 py-3 border-b border-gray-100">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping flex-shrink-0" />
                                  <p className="text-sm text-gray-700 font-medium truncate">
                                    {(() => {
                                      const steps = structuredProgress.plan_steps || [];
                                      const running = steps.filter(
                                        (s) => s.status === 'running',
                                      ).length;
                                      if (running > 1)
                                        return `Executing ${running} tasks in parallel${animatedDots}`;
                                      if (running === 1) return `Executing${animatedDots}`;
                                      return structuredProgress.message || 'Processing...';
                                    })()}
                                  </p>
                                </div>
                                {(() => {
                                  const steps = structuredProgress.plan_steps || [];
                                  const completed = steps.filter(
                                    (s) => s.status === 'completed' || s.status === 'skipped',
                                  ).length;
                                  const total = steps.length;
                                  if (total > 0) {
                                    const pct = Math.min(95, Math.round((completed / total) * 100));
                                    return (
                                      <span className="text-sm font-semibold text-[#07E5D1] ml-3 flex-shrink-0">
                                        {pct}%
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </div>

                            {(() => {
                              const planSteps = structuredProgress.plan_steps || [];
                              const executingComplete =
                                planSteps.length > 0 &&
                                planSteps.every(
                                  (s) => s.status === 'completed' || s.status === 'skipped',
                                );
                              const completedSteps = planSteps.filter(
                                (s) => s.status === 'completed' || s.status === 'skipped',
                              ).length;
                              if (planSteps.length === 0) return null;
                              return (
                                <div className="px-5 py-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        Executing Plan
                                      </span>
                                      {executingComplete && (
                                        <CheckCircle2 className="w-3 h-3 text-[#07E5D1]" />
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-400">
                                      {completedSteps}/{planSteps.length} done
                                    </span>
                                  </div>
                                  <ul className="space-y-2 ml-1">
                                    {planSteps.map((step, idx) => (
                                      <li
                                        key={`${step.id}-${idx}`}
                                        className={`transition-all duration-300 ${
                                          step.status === 'running'
                                            ? 'bg-[#E8FCFA] rounded-lg p-2.5 -mx-1.5 border border-[#07E5D1]/20'
                                            : 'px-0.5'
                                        }`}
                                      >
                                        <div className="flex items-start gap-2.5">
                                          <div className="flex-shrink-0 mt-0.5">
                                            {step.status === 'completed' && (
                                              <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
                                            )}
                                            {step.status === 'running' && (
                                              <div className="relative">
                                                <Circle className="w-4 h-4 text-[#07E5D1]" />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                  <div className="w-1.5 h-1.5 bg-[#07E5D1] rounded-full animate-ping" />
                                                </div>
                                              </div>
                                            )}
                                            {(step.status === 'pending' ||
                                              step.status === 'skipped') && (
                                              <Circle className="w-4 h-4 text-gray-300" />
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <span
                                              className={`text-sm leading-tight block ${
                                                step.status === 'completed'
                                                  ? 'text-gray-500 line-through'
                                                  : step.status === 'running'
                                                    ? 'text-gray-800 font-medium'
                                                    : 'text-gray-500'
                                              }`}
                                            >
                                              {step.label}
                                            </span>
                                            {step.status === 'running' && (
                                              <TimeBasedProgressBar
                                                taskId={step.id}
                                                startTime={stepStartTimes[step.id]}
                                                isCompleted={false}
                                              />
                                            )}
                                          </div>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-md px-5 py-4 shadow-sm">
                            <div className="flex items-center gap-3 mb-1">
                              <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                              <span className="text-sm font-medium text-[#374151]">
                                {progressMessage}
                              </span>
                            </div>
                            <p className="text-xs text-[#9CA3AF] ml-7">This may take a moment</p>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="h-1" />
                  </div>
                )}
              </div>
            </div>

            {/* Input */}
            <div className="bg-white border-t border-[#F1F5F9] px-6 py-3">
              <div className="max-w-[960px] mx-auto flex items-center gap-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    hasActiveForm
                      ? 'Fill in the inputs above to continue…'
                      : simulationStatus === 'running'
                        ? 'Simulation is running…'
                        : simulationStatus === 'failed'
                          ? 'Simulation failed — ask anything to continue…'
                          : 'Ask anything…'
                  }
                  disabled={inputDisabled}
                  className="flex-1 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFBFC] text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1] focus:bg-white transition-all disabled:bg-gray-100 disabled:text-[#9CA3AF] disabled:cursor-not-allowed"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || inputDisabled}
                  className="p-3 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </main>

          {/* Report panel — right half when open */}
          {reportPanelOpen && simulationId && simulationStatus === 'completed' && (
            <div className="w-1/2 flex-shrink-0 border-l border-[#F1F5F9] bg-white flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F1F5F9] flex-shrink-0">
                <div className="flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-[#07E5D1]" />
                  <span className="text-sm font-semibold text-[#1C1C1E]">Report</span>
                </div>
                <button
                  onClick={() => setReportPanelOpen(false)}
                  className="p-1 text-[#9CA3AF] hover:text-[#374151] transition-colors rounded"
                  title="Minimize report"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SimulationResultsContent simulationId={simulationId} hideChat hideHeader />
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deletingSessionId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={handleCancelDelete}
            />
            <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-[#1C1C1E] mb-2">Delete Chat?</h3>
              <p className="text-sm text-[#6B7280] mb-6">
                Are you sure you want to delete this chat session? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 text-sm font-medium text-[#6B7280] hover:text-[#374151] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#EF4444] hover:bg-[#DC2626] rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {shareData && (
          <ChatShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            shareUrl={shareData.share_url}
            messageCount={shareData.message_count}
            viewCount={shareData.view_count}
            forkCount={shareData.fork_count}
            onRevoke={handleRevokeShare}
            isRevoking={isRevokingShare}
          />
        )}
      </div>
    </div>
  );
};

export default ChatWithBluepill;
